import classnames from "classnames";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import Icon from "../../Icon";
import DateInput from "../DateInput";
import RadioButton from "../RadioButton";
import TextInput from "../TextInput";

import "./groupField.scss";

const GroupField = ({
  name,
  fieldArrayName,
  fieldIndex,
  inputFields,
  className,
  focusIndexRef,
  elementToFocus,
}) => {
  const {
    control,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const fieldArrayValues = useWatch({
    control,
    name,
  });

  const isAllFieldsEmpty =
    fieldArrayValues &&
    Object.values(fieldArrayValues).every((value) => {
      return value === "" || typeof value === "boolean";
    });
  const isSomeFieldsEmpty =
    fieldArrayValues &&
    !isAllFieldsEmpty &&
    Object.values(fieldArrayValues).some((value) => value === "");

  const currentError = errors[fieldArrayName]?.[fieldIndex];
  const hasRowError = currentError !== null && !!currentError;
  const hasRowComplete = !isAllFieldsEmpty && !isSomeFieldsEmpty; // All inputs have been filled in
  const hasRowClean = !hasRowError && isAllFieldsEmpty; // All inputs have not filled in
  const hasRowDirty = !isAllFieldsEmpty; // Some fields have been filled in

  useEffect(() => {
    if (currentError && isAllFieldsEmpty) {
      clearErrors(name);
    }
  }, [
    currentError,
    isAllFieldsEmpty,
    clearErrors,
    name,
    focusIndexRef,
    fieldIndex,
    hasRowClean,
    elementToFocus,
  ]);

  return (
    <fieldset
      name={name}
      className={classnames("form-group-field", className, {
        "has-error": hasRowError && hasRowDirty,
        "js-is-clean": hasRowClean,
        "js-is-dirty": hasRowDirty,
      })}
      ref={focusIndexRef}
    >
      <div className="form-group-field__inputs">
        {inputFields.map((inputField, idx) => {
          const inputOptions = {
            ...inputField.options,
            required: hasRowClean ? false : inputField.options.required,
            showAsterisk: inputField.options.required,
          };

          if (inputField.type === "date") {
            const datePickerOptions = {
              required: inputField?.options?.required,
            };
            return (
              <DateInput
                key={idx}
                name={`${name}.${inputField.name}`}
                label={inputField.label}
                options={inputOptions}
                isGroupFieldInput={true}
                groupFieldIndex={fieldIndex}
                groupFieldName={fieldArrayName}
                shouldRevalidate={hasRowDirty}
                datePickerOptions={datePickerOptions}
              />
            );
          }

          if (inputField.type === "radio") {
            return (
              <RadioButton
                key={idx}
                name={`${name}.${inputField.name}`}
                options={inputOptions}
                fieldIndex={fieldIndex}
              />
            );
          }

          return (
            <TextInput
              key={idx}
              name={`${name}.${inputField.name}`}
              label={inputField.label}
              options={inputOptions}
              isGroupFieldInput={true}
              groupFieldIndex={fieldIndex}
              groupFieldName={fieldArrayName}
              shouldRevalidate={hasRowDirty}
            />
          );
        })}
      </div>
      <div className="form-group-field__state">
        {hasRowComplete && !hasRowError && (
          <Icon name="system-checkmark" theme="success" aria-hidden="true" />
        )}
        {hasRowError && (
          <Icon name="system-warning" theme="error" aria-hidden="true" />
        )}
      </div>
    </fieldset>
  );
};

GroupField.propTypes = {
  name: PropTypes.string,
  fieldArrayName: PropTypes.string,
  fieldIndex: PropTypes.number,
  inputFields: PropTypes.array,
  className: PropTypes.string,
  focusIndexRef: PropTypes.oneOfType([PropTypes.element, PropTypes.object]),
  elementToFocus: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.object,
    PropTypes.node,
  ]),
};

GroupField.defaultProps = {
  fieldIndex: 0,
  inputFields: [],
};

export default GroupField;
